import { useState, useEffect } from "react";
import SecondaryBtn from "../button/SecondaryBtn";
import PrimaryBtn from "../button/PrimaryBtn";
import { useTranslation } from "react-i18next";

interface Lifeline {
  lifeline_id: {
    name: { en: string; ar: string };
    description: { en: string; ar: string };
    image: string;
    _id: string;
  };
  is_used: boolean;
  _id: string;
}

interface Category {
  _id: string;
  category_id?: {
    name: { en: string; ar: string };
    image: any;
    _id: string;
  };
  questions?: any[];
}

interface SwitchCategoryModalProps {
  isOpen: boolean;
  lifeline: Lifeline | null;
  teamId: string;
  teamName: string;
  categories: Category[];
  onConfirm: (selectedCategoryId: string) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const SwitchCategoryModal = ({
  isOpen,
  lifeline,
  teamId,
  teamName,
  categories,
  onConfirm,
  onClose,
  isLoading = false,
}: SwitchCategoryModalProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || "en") as "en" | "ar";
  const baseUrl = import.meta.env.VITE_BASE_CDN_URL;

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  // Reset selection when modal closes/opens or team changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCategoryId(null);
      return;
    }

    // Auto-select first available category when opening
    if (isOpen && categories && categories.length > 0) {
      setSelectedCategoryId(categories[0]?.category_id?._id || "");
    }
  }, [isOpen, teamId, categories]);

  if (!isOpen || !lifeline) return null;

  const lifelineData = lifeline.lifeline_id;
  const lifelineName = lifelineData?.name?.[currentLanguage] || lifelineData?.name?.en || "Switch";
  const lifelineDescription =
    lifelineData?.description?.[currentLanguage] || lifelineData?.description?.en || "";
  const lifelineImage = lifelineData?.image ? `${baseUrl}/${lifelineData.image}` : "";

  const getCategoryDisplayData = (category: Category) => {
    const name =
      category.category_id?.name?.[currentLanguage] ||
      category.category_id?.name?.en ||
      t("switchCategory.category") ||
      "Category";

    const imageSource = category.category_id?.image
      ? typeof category.category_id.image === "string"
        ? `${baseUrl}/${category.category_id.image}`
        : category.category_id.image[currentLanguage]
          ? `${baseUrl}/${category.category_id.image[currentLanguage]}`
          : ""
      : "";

    return { name, imageSource };
  };

  const handleConfirm = () => {
    if (selectedCategoryId) {
      onConfirm(selectedCategoryId);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-5 z-50">
      <div className="modal bg-white-500 2xl:rounded-[2.083vw] xl:rounded-[2.083vw] lg:rounded-[2.5vw] rounded-3xl 2xl:p-[1.5vw] xl:p-[1.5vw] lg:p-[2vw] md:p-5 p-3 2xl:max-w-[35vw] xl:max-w-[38vw] lg:max-w-[45vw] sm:max-w-[75vw] max-w-[90vw] 2xl:shadow-[0px_0px_3.854vw_0px_#C3DC78CC_inset] shadow-[0px_0px_3.854vw_0px_#C3DC78CC_inset] w-full relative animate-[bounceIn_0.4s_ease-out] 2xl:border-[0.2vw] lg:border-[0.25vw] border-3 border-primary auth-page overflow-y-auto lg:max-h-[calc(100dvh-5%)] max-h-[calc(100dvh-20px)] hide-scroll">
        {/* Header - Title and Close Button */}
        <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full 2xl:mb-[0.5vw] md:mb-2 mb-2">
          <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/6 text-2xl/5 font-semibold text-primary-orange">
            {lifelineName}
          </h1>
          {/* Close Button */}
          <button
            onClick={onClose}
            className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 shrink-0"
          >
            <svg
              className="w-full h-full"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                fill="#333938"
              />
            </svg>
          </button>
        </div>

        {/* Divider Line */}
        <div className="w-full 2xl:mb-[0.6vw] mb-3">
          <svg
            className="w-full"
            width="540"
            height="1"
            viewBox="0 0 540 1"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              y1="-0.5"
              x2="540"
              y2="-0.5"
              transform="matrix(-1 0 0 1 540 1)"
              stroke="url(#paint0_linear_switch_category)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_switch_category"
                x1="0"
                y1="0.5"
                x2="540"
                y2="0.5"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0B3369" stopOpacity="0" />
                <stop offset="0.5" stopColor="#7364FF" />
                <stop offset="1" stopColor="#0B3369" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Lifeline Icon */}
        <div className="2xl:w-[4vw] 2xl:h-[4vw] xl:w-16 w-12 xl:h-16 h-12 flex items-center justify-center 2xl:mb-[0.5vw] mb-2 mx-auto">
          {lifelineImage && (
            <img
              src={lifelineImage}
              alt={lifelineName}
              className="w-full h-full object-contain mx-auto drop-shadow-lg"
            />
          )}
        </div>

        {/* Lifeline Name */}
        <h2 className="2xl:text-[1.2vw]/tight xl:text-xl/tight text-lg/tight font-bold text-center text-black 2xl:mb-[0.3vw] mb-1.5 font-garamond">
          {lifelineName}
        </h2>

        {/* Team Name Badge */}
        <div className="flex justify-center 2xl:mb-[0.4vw] mb-2">
          <span className="inline-block 2xl:px-[0.833vw] px-3 2xl:py-[0.25vw] py-1 bg-primary-ivory lg:border-[0.13vw] border-2 border-transparent rounded-full text-black-300 2xl:text-[0.729vw] text-xs font-medium">
            {teamName}
          </span>
        </div>

        {/* Lifeline Description */}
        <p className="text-center text-gray-600 2xl:text-[0.75vw]/snug text-sm/snug 2xl:mb-[0.6vw] md:mb-3 mb-2 px-2">
          {lifelineDescription}
        </p>

        {/* Category Selection */}
        <div className="2xl:mb-[0.6vw] mb-2">
          <h3 className="2xl:text-[0.8vw] text-sm font-semibold text-black-300 2xl:mb-[0.3vw] mb-1.5 text-center">
            {t("switchCategory.selectCategory")}
          </h3>
          {categories.length === 0 ? (
            <p className="text-center text-gray-500 2xl:text-[0.7vw] text-xs">
              {t("switchCategory.noCategoriesAvailable") || "No categories available to switch."}
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-3 2xl:mb-[0.8vw] mb-3">
              {categories.map((category) => {
                const { name, imageSource } = getCategoryDisplayData(category);
                const isSelected = selectedCategoryId === category?.category_id?._id;
                if (category?.category_id?.name?.en == "Mystery Wildcard") return null;
                return (
                  <button
                    key={category?.category_id?._id}
                    onClick={() => setSelectedCategoryId(isSelected ? null : category?.category_id?._id)}
                    className={`h-full flex items-center justify-center relative transition-all duration-300 rounded-lg
                      ${isSelected
                        ? "bg-primary-light-orange border-primary-orange cursor-pointer"
                        : "bg-transparent border-black-300 cursor-pointer hover:bg-primary-yellow"
                      }`}
                  >
                    {imageSource && (
                      <img
                        src={imageSource}
                        alt={name}
                        className="w-full h-full object-contain "
                      />
                    )}
                    {/* <span className="2xl:text-[0.7vw]/tight xl:text-xs/tight md:text-xs/tight text-[10px]/tight font-bold text-center">
                      {name}
                    </span> */}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <SecondaryBtn onClick={onClose} to="" className="xs:w-1/2 max-w-full lg:mb-0 md:mb-0 mb-0">
            {t("lifelineConfirmModal.cancel")}
          </SecondaryBtn>
          <PrimaryBtn
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={!selectedCategoryId || categories.length === 0}
            className="flex-1 max-w-full!"
          >
            {t("lifelineConfirmModal.confirm")}
          </PrimaryBtn>
        </div>
      </div>

      <style>{`
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default SwitchCategoryModal;

